/**
 * Reacting to a message.
 *
 * A reaction is the viewer's own, per emoji: the server records one per (message, person,
 * emoji) and both writes are idempotent, so this module exposes one `toggleReaction` and
 * callers never hold the "did I already react?" question — it is answered here, from the
 * viewer's own pending clicks first and the server's last answer second.
 *
 * **The pill moves on the click, not on the response.** A click records an INTENT — "the
 * viewer wants to be in this emoji's group" — in `use-pending-reactions`, and the pills
 * draw the cache with those intents applied. A reaction write is a multi-hundred-ms round
 * trip, and a pill that waits for it reads as a broken button.
 *
 * Intents, rather than a patch written into the query cache, because the cache is where
 * the server's answers live: a response that was already stale when it landed, or any
 * thread refetch, overwrites a patch and takes a newer click with it. That full argument
 * is `use-pending-reactions`'s module doc. What it buys here is that **nothing the server
 * says can erase a click that has not been answered yet**, so a burst stays on screen
 * exactly as it was clicked.
 *
 * **One write per message is in flight at a time, and a burst is batched into it.** Both
 * routes answer with the whole message's reactions as of their own commit, so two
 * concurrent writes mean the first answer is stale on arrival and the server can commit a
 * same-emoji add and remove in the wrong order. `drain` holds a lock per message and
 * loops until the server agrees with every intent on it, re-reading the intents each pass
 * so it only ever sends the END state — five taps on one emoji are one request, not five.
 * Past the first write it waits out a quiet window before the next one, which keeps a
 * burst from spending a round trip — and a realtime echo, and the refetch that echo
 * provokes in this very tab — on every single tap.
 *
 * Nothing is disabled while a write is in flight: the intent already shows the outcome,
 * so a second click is a second real toggle rather than a double-submit, and the pills
 * stay answerable at the speed someone can click them.
 *
 * Nothing is refetched afterwards. A reaction touches nothing but the message's own
 * reactions — the platform service leaves `Message.updatedAt` alone on purpose — and the
 * response IS the new value, so the thread, the channel row, members, unread state and
 * the prompt blocks are all still current. Other tabs hear the route's `message.changed`.
 */

import { queryKeys } from "@birdwatch/client";
import { toast } from "@birdwatch/ui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  clearReactionIntents,
  readReactionIntents,
  setReactionIntent,
  settleReactionIntentIfSeq,
} from "../_hooks/use-pending-reactions";
import {
  firstUnsettledIntent,
  isViewerReactionIntended,
} from "../_libs/pending-reactions";
import {
  type ChannelMessage,
  type ChannelMessageReaction,
  type ChannelMessageReactionState,
  useCreateChannelsMessagesReactionsByCidByMessageId,
  useDeleteChannelsMessagesReactionsByCidByMessageIdByType,
} from "./channel-endpoints";
import { useChannelMessageCache } from "./use-channel-message-cache";

/**
 * How long a reaction write may hang before it is abandoned.
 *
 * Load-bearing, not hygiene: `drain` releases its lock in a `finally`, so a request that
 * never settles would leave the lock claimed and the intent painted for the life of the
 * page, silently swallowing every later click on that message.
 */
const REACTION_WRITE_TIMEOUT_MS = 10_000;

/**
 * How long the write loop waits for a burst to stop before spending another round trip.
 *
 * Only BETWEEN writes, never before the first: a lone click still reaches the server at
 * once, so nothing is at risk if the tab closes a moment later. What the window buys is
 * the taps arriving while the previous write is already answering — they collapse into
 * the next write instead of each taking a request of their own. Long enough to span a
 * double tap, short enough to be invisible behind a pill that already moved.
 */
const REACTION_WRITE_BATCH_MS = 300;

/** Resolves after `ms` — the loop's quiet window, see {@link REACTION_WRITE_BATCH_MS}. */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * The messages with a write loop running, so a click that arrives mid-flight records its
 * intent and returns rather than starting a second loop.
 *
 * Module-level, like the intents themselves: the loop outlives the component that started
 * it (a channel switch mid-write must still bank the server's answer and release the
 * lock), and the same message is rendered by more than one surface.
 */
const inFlightMessages = new Set<string>();

function lockKey(cid: string, messageId: string): string {
  return `${cid}:${messageId}`;
}

/** What a pill or an overflow row does with a click. Fire-and-forget. */
export type ToggleReaction = (message: ChannelMessage, type: string) => void;

export function useChannelReactions(cid: string, viewerId: string | undefined) {
  const queryClient = useQueryClient();
  const patchMessage = useChannelMessageCache(cid);

  const writeReactions = useCallback(
    (messageId: string, reactions: ChannelMessageReaction[]) =>
      patchMessage(messageId, (message) => ({ ...message, reactions })),
    [patchMessage],
  );

  const addMutation = useCreateChannelsMessagesReactionsByCidByMessageId({
    timeoutMs: REACTION_WRITE_TIMEOUT_MS,
    onSuccess: (state: ChannelMessageReactionState) =>
      writeReactions(state.id, state.reactions),
  });
  const removeMutation =
    useDeleteChannelsMessagesReactionsByCidByMessageIdByType({
      timeoutMs: REACTION_WRITE_TIMEOUT_MS,
      onSuccess: (state: ChannelMessageReactionState) =>
        writeReactions(state.id, state.reactions),
    });

  /** The one write that makes `reacted` true for this emoji — the route is the detail. */
  const writeIntent = useCallback(
    async (
      messageId: string,
      type: string,
      reacted: boolean,
    ): Promise<void> => {
      if (reacted) {
        await addMutation.mutateAsync({
          params: { cid, messageId },
          body: { type },
        });
        return;
      }
      // The emoji is a path segment, and the generated client substitutes params
      // verbatim — so it is encoded here; the platform router decodes it.
      await removeMutation.mutateAsync({
        params: { cid, messageId, type: encodeURIComponent(type) },
      });
    },
    [addMutation, removeMutation, cid],
  );

  /**
   * Write this message's intents until the server agrees with them, one at a time.
   *
   * **Nothing in here may read React state or a ref.** It outlives its component by
   * design — the store, the lock and the toast are module-level, and `queryClient` is the
   * app's.
   */
  const drain = useCallback(
    async (messageId: string): Promise<void> => {
      const key = lockKey(cid, messageId);
      // The caller has ALREADY recorded its intent, so a loop that is still running will
      // see it on its next pass. Set-then-test is the whole handshake.
      if (inFlightMessages.has(key)) return;
      inFlightMessages.add(key);

      try {
        let isFirstWrite = true;

        while (true) {
          // Let the burst finish arriving before spending another round trip on it. Also
          // this loop's exit delay, deliberately: a tap landing in the window is served
          // by THIS loop rather than starting a new one a moment later.
          if (!isFirstWrite) await wait(REACTION_WRITE_BATCH_MS);

          // Re-read every pass, so a burst collapses: on -> off -> on across one window
          // issues ONE write for the final state, not three.
          const next = firstUnsettledIntent(
            readReactionIntents(cid, messageId),
          );
          if (!next) break;
          const [type, intent] = next;
          isFirstWrite = false;

          // The request names the END STATE, not the change. Both routes are idempotent
          // (`platform/src/resources/channels/channel-reaction.repository.ts` inserts
          // `onConflict(...).ignore()` and deletes unguarded), so a write the server
          // already agrees with costs a round trip and changes nothing.
          await writeIntent(messageId, type, intent.reacted);

          // The answer is in the cache already (the mutations' `onSuccess`). Anything
          // still fetching read this message BEFORE the write committed, so its answer
          // would un-draw the reaction a moment after it landed. Cancelling HERE rather
          // than at the click also catches the fetches issued DURING the round trip. The
          // cost is that the cid prefix catches an in-flight "load older" page too; it
          // re-triggers on scroll.
          void queryClient.cancelQueries({
            queryKey: queryKeys.getChannelsMessagesByCid({ cid }),
          });

          // Settled, NOT forgotten: the intent keeps painting the pill until a bubble
          // renders reactions that carry it (`reapLandedReactionIntents`). No `await`
          // between the read above and the release below, which is what makes the
          // set-then-test handshake airtight — a click can only land while this loop is
          // suspended, and the next pass sees it.
          settleReactionIntentIfSeq(cid, messageId, type, intent.seq);
        }
      } catch {
        // The intents ARE the optimistic state, so dropping them is the whole rollback —
        // there is no snapshot to restore, and therefore none that can already be stale
        // by the time it is restored.
        clearReactionIntents(cid, messageId);
        toast.error({ title: "Couldn't update the reaction" });
      } finally {
        inFlightMessages.delete(key);
      }
    },
    [writeIntent, queryClient, cid],
  );

  /** What the viewer's state for this emoji is RIGHT NOW — intent first, cache second. */
  const currentlyReacted = useCallback(
    (message: ChannelMessage, type: string): boolean =>
      !!viewerId &&
      // `readReactionIntents`, never the render snapshot: two clicks in one frame share
      // one `message` prop, so the second can only answer correctly by reading what the
      // first just intended.
      isViewerReactionIntended(
        message.reactions,
        readReactionIntents(cid, message.id),
        type,
        viewerId,
      ),
    [cid, viewerId],
  );

  /**
   * The one way in, for the pills and for the picker alike: both reach the same control,
   * so a pick on an emoji the viewer already used takes it back exactly as clicking its
   * pill would.
   *
   * The direction is decided HERE and nowhere else, because this is the only place that
   * can see the viewer's live intents — a caller comparing the pick against the pills it
   * rendered would be reading a snapshot that a click one frame earlier has already
   * invalidated.
   */
  const toggleReaction = useCallback<ToggleReaction>(
    (message, type) => {
      // Signed out, or `me` still loading. There is nobody to attribute an intent to and
      // every emoji reads as "not reacted", so the only honest write is the add — and the
      // pills wait for its answer, exactly as they always have.
      if (!viewerId) {
        void addMutation
          .mutateAsync({
            params: { cid, messageId: message.id },
            body: { type },
          })
          .catch(() => toast.error({ title: "Couldn't update the reaction" }));
        return;
      }

      const reacted = !currentlyReacted(message, type);
      setReactionIntent(cid, message.id, type, reacted); // this alone paints
      void drain(message.id);
    },
    [addMutation, currentlyReacted, drain, cid, viewerId],
  );

  return { toggleReaction };
}
